import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Filter, Plus, Search, ShoppingCart, Truck, X } from "lucide-react";

import { useCreatePurchaseOrder, useCreateSupplier, usePurchaseOrders, useSuppliers } from "@/api/queries";
import type { PurchaseOrder, PurchaseOrderStatus } from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDate, formatMoney, formatPurchaseOrderStatus } from "@/lib/format";
import { purchaseOrderTone } from "@/lib/stock";

const PAGE_SIZE = 50;

const STATUSES: PurchaseOrderStatus[] = [
  "DRAFT",
  "ORDERED",
  "PARTIALLY_RECEIVED",
  "RECEIVED",
  "CANCELLED",
];

/**
 * Purchase orders (Frontend Spec §19, PHASE 10).
 *
 * The status column is computed by the server from what has actually arrived,
 * never set by hand — an order cannot say "Reçu" while a line is still short.
 */
export function PurchasingPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get("q") ?? "");
  const [debounced, setDebounced] = useState(term);
  const [creating, setCreating] = useState(false);

  const status = params.get("status") ?? undefined;
  const offset = Number(params.get("offset") ?? 0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [term]);

  const query = useMemo(
    () => ({
      search: debounced || undefined,
      status: status as PurchaseOrderStatus | undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [debounced, status, offset],
  );

  const { data, isLoading, error, refetch } = usePurchaseOrders(query);

  function update(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    if (!("offset" in next)) merged.delete("offset");
    setParams(merged, { replace: true });
  }

  const hasFilters = Boolean(debounced || status);

  return (
    <Page>
      <PageHeader
        title="Achats"
        subtitle="Ce qui est commandé, ce qui est arrivé, et ce qui est encore dû."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate("/receiving")}>
              <Truck className="size-4" aria-hidden />
              Réception
            </Button>
            <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden />
              Nouvelle commande
            </Button>
          </>
        }
      />

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden
            />
            <Input
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                update({ q: event.target.value, offset: undefined });
              }}
              placeholder="Code, référence, fournisseur…"
              className="pl-9"
              aria-label="Rechercher une commande"
            />
          </div>

          <Select
            value={status ?? ""}
            onChange={(event) => update({ status: event.target.value || undefined })}
            aria-label="Filtrer par statut"
            className="sm:w-56"
          >
            <option value="">Tous les statuts</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {formatPurchaseOrderStatus(value)}
              </option>
            ))}
          </Select>

          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setTerm("");
                setParams(new URLSearchParams(), { replace: true });
              }}
              className="flex h-10 items-center gap-1.5 rounded-md px-2 text-[13px] text-ink-subtle hover:text-ink"
            >
              <X className="size-4" aria-hidden />
              Effacer
            </button>
          )}
        </div>

        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={6} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={
                hasFilters ? (
                  <Filter className="size-6" aria-hidden />
                ) : (
                  <ShoppingCart className="size-6" aria-hidden />
                )
              }
              title={hasFilters ? "Aucune commande ne correspond" : "Aucune commande"}
              description={
                hasFilters
                  ? "Essayez un autre terme, ou retirez les filtres."
                  : "Créez une commande, ajoutez ses lignes, puis recevez la livraison depuis l'écran Réception."
              }
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="w-28 whitespace-nowrap">Code</Th>
                      <Th className="max-w-0">Fournisseur</Th>
                      <Th className="hidden max-w-0 @4xl:table-cell">Référence</Th>
                      <Th className="hidden whitespace-nowrap @5xl:table-cell">Attendu</Th>
                      <Th align="right" className="hidden whitespace-nowrap @3xl:table-cell">
                        Lignes
                      </Th>
                      <Th align="right" className="whitespace-nowrap">
                        Total
                      </Th>
                      <Th>Statut</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((order) => (
                      <OrderRow key={order.id} order={order} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((order) => (
                  <li key={order.id}>
                    <OrderCard order={order} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={(next) => update({ offset: String(next) })}
          />
        )}
      </div>

      <NewOrderDialog open={creating} onOpenChange={setCreating} />
    </Page>
  );
}

function OrderRow({ order }: { order: PurchaseOrder }) {
  return (
    <Tr>
      <Td className="whitespace-nowrap font-mono text-[12px] text-ink-muted">
        <Link to={`/purchase-orders/${order.id}`} className="hover:text-brand">
          {order.code}
        </Link>
      </Td>
      <Td className="max-w-0">
        <Link
          to={`/purchase-orders/${order.id}`}
          className="block truncate font-medium text-ink hover:text-brand"
        >
          {order.supplier_name}
        </Link>
      </Td>
      <Td className="hidden max-w-0 truncate text-[13px] text-ink-muted @4xl:table-cell">
        {order.reference ?? "—"}
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-muted @5xl:table-cell">
        {formatDate(order.expected_on)}
      </Td>
      <Td align="right" className="tnum hidden text-[13px] text-ink-muted @3xl:table-cell">
        {order.line_count}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink">
        {formatMoney(order.total)}
      </Td>
      <Td>
        <StatusBadge tone={purchaseOrderTone(order.status)}>
          {formatPurchaseOrderStatus(order.status)}
        </StatusBadge>
      </Td>
    </Tr>
  );
}

function OrderCard({ order }: { order: PurchaseOrder }) {
  return (
    <Link
      to={`/purchase-orders/${order.id}`}
      className="flex items-center gap-3 px-3 py-3 active:bg-surface-hover"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">{order.supplier_name}</p>
        <p className="truncate font-mono text-[11px] text-ink-subtle">
          {order.code}
          {order.reference ? ` · ${order.reference}` : ""}
        </p>
        <p className="tnum mt-0.5 truncate text-[12px] text-ink-muted">
          {formatMoney(order.total)}
        </p>
      </div>
      <StatusBadge tone={purchaseOrderTone(order.status)} className="shrink-0">
        {formatPurchaseOrderStatus(order.status)}
      </StatusBadge>
    </Link>
  );
}

function NewOrderDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const createOrder = useCreatePurchaseOrder();
  const createSupplier = useCreateSupplier();
  const { data: suppliers } = useSuppliers({ limit: 200 }, { enabled: open });

  const [supplierId, setSupplierId] = useState("");
  const [reference, setReference] = useState("");
  const [expectedOn, setExpectedOn] = useState("");
  const [newSupplier, setNewSupplier] = useState("");
  const [addingSupplier, setAddingSupplier] = useState(false);

  function reset() {
    setSupplierId("");
    setReference("");
    setExpectedOn("");
    setNewSupplier("");
    setAddingSupplier(false);
  }

  async function addSupplier() {
    const name = newSupplier.trim();
    if (!name) return;
    try {
      const supplier = await createSupplier.mutateAsync({ name });
      setSupplierId(supplier.id);
      setNewSupplier("");
      setAddingSupplier(false);
    } catch (error) {
      toast.error("Fournisseur non créé", String(error));
    }
  }

  async function submit() {
    if (!supplierId) return;
    try {
      const order = await createOrder.mutateAsync({
        supplier_id: supplierId,
        reference,
        expected_on: expectedOn || null,
      });
      toast.success("Commande créée", `${order.code} — ${order.supplier_name}`);
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error("Commande non créée", String(error));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Nouvelle commande"
      description="Elle démarre en brouillon. Ajoutez ses lignes, puis envoyez-la."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!supplierId || createOrder.isPending}
          >
            {createOrder.isPending ? "Création…" : "Créer"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Fournisseur">
          {addingSupplier ? (
            <div className="flex gap-2">
              <Input
                value={newSupplier}
                onChange={(event) => setNewSupplier(event.target.value)}
                placeholder="Nom du fournisseur"
                aria-label="Nom du nouveau fournisseur"
              />
              <Button
                variant="secondary"
                onClick={() => void addSupplier()}
                disabled={!newSupplier.trim() || createSupplier.isPending}
              >
                Ajouter
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Select
                value={supplierId}
                onChange={(event) => setSupplierId(event.target.value)}
                aria-label="Fournisseur"
              >
                <option value="">Choisir un fournisseur…</option>
                {suppliers?.items.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </Select>
              <Button
                variant="ghost"
                onClick={() => setAddingSupplier(true)}
                aria-label="Nouveau fournisseur"
              >
                <Plus className="size-4" aria-hidden />
              </Button>
            </div>
          )}
          {suppliers && suppliers.items.length === 0 && !addingSupplier && (
            <p className="mt-1 text-[12px] text-ink-subtle">
              Aucun fournisseur enregistré. Ajoutez-en un avec le bouton à droite.
            </p>
          )}
        </Field>

        <Field label="Référence" hint="Facultatif. Votre numéro de bon de commande.">
          <Input
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            aria-label="Référence de la commande"
          />
        </Field>

        <Field label="Livraison attendue" hint="Facultatif.">
          <Input
            type="date"
            value={expectedOn}
            onChange={(event) => setExpectedOn(event.target.value)}
            aria-label="Date de livraison attendue"
          />
        </Field>
      </div>
    </Dialog>
  );
}
